import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { queryEventsList } from '../schema';
import { useState, useMemo } from 'react';
import { db } from '../instant';
import { id } from '@instantdb/react';
import { computeItemsToCopy } from '../utils/copyPackingList';
import { deriveCopyPackingListViewState } from '../utils/copyPackingListViewState';
import { usePackingListsByEvent } from '../hooks/usePackingListsByEvent';

interface Props {
    open: boolean;
    onClose: () => void;
    eventId: string;
    onCopied: () => void;
}

export default function CopyPackingListDialog({ open, onClose, eventId, onCopied }: Props) {
    const { data: eventsData, isLoading: eventsLoading } = queryEventsList(open);
    const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
    const [includeWishList, setIncludeWishList] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    const events = useMemo(() => (eventsData?.events ?? []).filter(e => e.id !== eventId), [eventsData, eventId]);

    const {
        packingListItemsByEvent,
        eventsWithPackingLists,
        isLoading: packingListsLoading,
        error: packingListsError,
    } = usePackingListsByEvent(events, { enabled: open });

    const eventsWithCounts = useMemo(() => {
        return eventsWithPackingLists.map(event => {
            const packingListItems = packingListItemsByEvent.get(event.id) ?? [];
            const trailers = packingListItems.filter(item => item.itemType?.category?.name === 'Trailers');
            const otherItems = packingListItems.filter(item => item.itemType?.category?.name !== 'Trailers');

            return {
                event,
                itemCount: otherItems.length,
                trailerCount: trailers.reduce((sum, item) => sum + (item.quantity || 0), 0),
                packingListItems,
            };
        });
    }, [eventsWithPackingLists, packingListItemsByEvent]);

    const viewState = deriveCopyPackingListViewState({
        eventsLoading,
        packingListsLoading,
        packingListsError,
        eventCount: eventsWithCounts.length,
    });

    const selectedEvent = eventsWithCounts.find(e => e.event.id === selectedEventId);

    const handleCopy = async () => {
        if (!selectedEvent) return;

        setIsCopying(true);

        try {
            const packByItemType = new Map<string, number>();
            for (const item of selectedEvent.packingListItems) {
                const itemTypeId = item.itemType?.id;
                if (!itemTypeId) continue;
                const qty = item.quantity || 0;
                packByItemType.set(itemTypeId, (packByItemType.get(itemTypeId) ?? 0) + qty);
            }

            let wishByItemType = new Map<string, number>();
            if (includeWishList && selectedEventId) {
                const { data: wishListData } = await db.queryOnce({
                    events: {
                        $: {
                            where: {
                                id: selectedEventId,
                            },
                        },
                        wishListItems: {
                            itemType: {},
                        },
                    },
                });
                const wishListItems = wishListData?.events?.[0]?.wishListItems || [];
                for (const wishItem of wishListItems) {
                    const itemTypeId = wishItem.itemType?.id;
                    if (!itemTypeId) continue;
                    const qty = wishItem.quantity || 0;
                    wishByItemType.set(itemTypeId, (wishByItemType.get(itemTypeId) ?? 0) + qty);
                }
            }
            const itemsArray = computeItemsToCopy(packByItemType, wishByItemType, includeWishList && !!selectedEventId);

            const txs: any[] = [];
            for (const { itemTypeId, quantity } of itemsArray) {
                const newItemId = id();
                txs.push(
                    db.tx.packingListItems[newItemId].update({
                        quantity,
                        confirmed: false,
                    }),
                    db.tx.packingListItems[newItemId].link({ itemType: itemTypeId }),
                    db.tx.packingListItems[newItemId].link({ event: eventId })
                );
            }

            await db.transact(txs);
            onCopied();
            handleClose();
        } catch (error) {
            console.error('Error copying packing list:', error);
        } finally {
            setIsCopying(false);
        }
    };

    const handleClose = () => {
        setSelectedEventId(null);
        setIncludeWishList(false);
        onClose();
    };

    const formatDateRange = (event: { startDate?: string | number | null; endDate?: string | number | null }) => {
        if (!event.startDate && !event.endDate) return 'No dates';
        const start = event.startDate ? new Date(event.startDate).toLocaleDateString() : '';
        const end = event.endDate ? new Date(event.endDate).toLocaleDateString() : '';
        if (start && end) return `${start} - ${end}`;
        return start || end;
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Copy Packing List from Previous Event</Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                {viewState === 'loading' ? (
                    <Box display="flex" justifyContent="center" padding={4}>
                        <CircularProgress />
                    </Box>
                ) : viewState === 'error' ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            Could not load packing lists. Close this dialog and try again.
                        </Typography>
                    </Box>
                ) : viewState === 'empty' ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            No events with packing lists
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <List>
                                {eventsWithCounts.map(({ event, itemCount, trailerCount }) => (
                                    <ListItemButton
                                        key={event.id}
                                        selected={selectedEventId === event.id}
                                        onClick={() => setSelectedEventId(event.id)}
                                    >
                                        <ListItemText
                                            primary={event.name}
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDateRange(event)}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {itemCount} items, {trailerCount} {trailerCount === 1 ? 'trailer' : 'trailers'}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        </Paper>

                        {selectedEvent && (
                            <FormControlLabel
                                control={<Checkbox checked={includeWishList} onChange={e => setIncludeWishList(e.target.checked)} />}
                                label="Include wish list items from selected event"
                            />
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button variant="contained" onClick={handleCopy} disabled={!selectedEventId || isCopying}>
                    {isCopying ? 'Copying...' : 'Copy Packing List'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
